import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  RefreshCw,
  Database,
  Filter,
  Download,
  Settings,
  BarChart3,
  Activity,
  AlertTriangle
} from "lucide-react";
import { 
  PriceTrendAnalysis
} from "@/types";

const PriceTrends = () => {
  const [selectedPeriod, setSelectedPeriod] = useState("annual");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedGeoZone, setSelectedGeoZone] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const [trendAnalysis] = useState<PriceTrendAnalysis[]>([
    {
      id: "1",
      materialId: "CEM-001",
      geoZoneId: "LAG",
      analysisPeriod: "annual",
      startDate: "2023-01-01",
      endDate: "2023-12-31",
      trendDirection: "increasing",
      averageGrowthRate: 6.56,
      volatilityIndex: 2.1,
      seasonalityFactor: 1.05,
      forecastNextQuarter: 6100.00,
      confidenceLevel: 85,
      analysisData: {
        quarterlyGrowth: [7.78, 7.22, 5.77, 5.45],
        seasonalPattern: "q3_peak",
        volatilityReason: "construction_boom",
        forecastFactors: ["inflation", "demand", "transport_costs"]
      },
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "2",
      materialId: "CEM-001",
      geoZoneId: "KAN",
      analysisPeriod: "annual",
      startDate: "2023-01-01",
      endDate: "2023-12-31",
      trendDirection: "increasing",
      averageGrowthRate: 5.23,
      volatilityIndex: 1.8,
      seasonalityFactor: 1.02,
      forecastNextQuarter: 5050.00,
      confidenceLevel: 78,
      analysisData: {
        quarterlyGrowth: [6.12, 5.89, 4.95, 4.78],
        seasonalPattern: "q2_peak",
        volatilityReason: "stable_demand",
        forecastFactors: ["inflation", "demand"]
      },
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "3",
      materialId: "STEEL-001",
      geoZoneId: "LAG",
      analysisPeriod: "annual",
      startDate: "2023-01-01",
      endDate: "2023-12-31",
      trendDirection: "increasing",
      averageGrowthRate: 4.12,
      volatilityIndex: 3.2,
      seasonalityFactor: 1.08,
      forecastNextQuarter: 437500.00,
      confidenceLevel: 72,
      analysisData: {
        quarterlyGrowth: [5.23, 4.89, 3.45, 2.91],
        seasonalPattern: "q4_peak",
        volatilityReason: "import_dependency",
        forecastFactors: ["inflation", "demand", "transport_costs", "currency"]
      },
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "4",
      materialId: "STEEL-001",
      geoZoneId: "ABJ",
      analysisPeriod: "annual",
      startDate: "2023-01-01",
      endDate: "2023-12-31",
      trendDirection: "increasing",
      averageGrowthRate: 3.78,
      volatilityIndex: 2.8,
      seasonalityFactor: 1.06,
      forecastNextQuarter: 451000.00,
      confidenceLevel: 80,
      analysisData: {
        quarterlyGrowth: [4.56, 4.23, 3.12, 2.89],
        seasonalPattern: "q4_peak",
        volatilityReason: "import_dependency",
        forecastFactors: ["inflation", "demand", "transport_costs"]
      },
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "5",
      materialId: "AGG-001",
      geoZoneId: "LAG",
      analysisPeriod: "annual",
      startDate: "2023-01-01",
      endDate: "2023-12-31",
      trendDirection: "stable",
      averageGrowthRate: 1.23,
      volatilityIndex: 1.2,
      seasonalityFactor: 0.98,
      forecastNextQuarter: 18500.00,
      confidenceLevel: 92,
      analysisData: {
        quarterlyGrowth: [1.45, 1.23, 1.12, 0.98],
        seasonalPattern: "stable",
        volatilityReason: "local_supply",
        forecastFactors: ["inflation", "demand"]
      },
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    }
  ]);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'decreasing':
        return <TrendingDown className="h-4 w-4 text-red-600" />;
      default:
        return <Minus className="h-4 w-4 text-gray-600" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getMaterialName = (materialId: string) => {
    const materialNames: { [key: string]: string } = {
      'CEM-001': 'Cement (Grade 42.5)',
      'STEEL-001': 'Steel Reinforcement (12mm)',
      'AGG-001': 'Granite Aggregate (20mm)',
      'BLK-001': 'Concrete Block (9 inch)'
    };
    return materialNames[materialId] || materialId;
  };

  const getGeoZoneName = (geoZoneId: string) => {
    const geoZoneNames: { [key: string]: string } = {
      'LAG': 'Lagos',
      'ABJ': 'Abuja',
      'KAN': 'Kano',
      'PHC': 'Port Harcourt',
      'IBD': 'Ibadan',
      'KAD': 'Kaduna'
    };
    return geoZoneNames[geoZoneId] || geoZoneId;
  };

  const getVolatilityColor = (volatility: number) => {
    if (volatility >= 3.0) return "text-red-600";
    if (volatility >= 2.0) return "text-orange-600";
    if (volatility >= 1.5) return "text-yellow-600";
    return "text-green-600";
  };

  const filteredTrends = trendAnalysis.filter(trend => {
    const matchesPeriod = selectedPeriod === "all" || trend.analysisPeriod === selectedPeriod;
    const matchesCategory = selectedCategory === "all" || 
      (selectedCategory === "Cement" && trend.materialId.includes("CEM")) ||
      (selectedCategory === "Steel" && trend.materialId.includes("STEEL")) ||
      (selectedCategory === "Aggregates" && trend.materialId.includes("AGG")) ||
      (selectedCategory === "Blocks" && trend.materialId.includes("BLK"));
    const matchesGeoZone = selectedGeoZone === "all" || trend.geoZoneId === selectedGeoZone;
    const matchesSearch = searchTerm === "" || 
      getMaterialName(trend.materialId).toLowerCase().includes(searchTerm.toLowerCase()) ||
      getGeoZoneName(trend.geoZoneId).toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesPeriod && matchesCategory && matchesGeoZone && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Price Trend Analysis</h2>
          <p className="text-gray-600">Long-term price trends and forecasting</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Data
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filters & Search
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <Label htmlFor="period">Analysis Period</Label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Periods</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="category">Material Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Cement">Cement</SelectItem>
                  <SelectItem value="Steel">Steel</SelectItem>
                  <SelectItem value="Aggregates">Aggregates</SelectItem>
                  <SelectItem value="Blocks">Blocks</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="geoZone">Geo Zone</Label>
              <Select value={selectedGeoZone} onValueChange={setSelectedGeoZone}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Zones</SelectItem>
                  <SelectItem value="LAG">Lagos</SelectItem>
                  <SelectItem value="ABJ">Abuja</SelectItem>
                  <SelectItem value="KAN">Kano</SelectItem>
                  <SelectItem value="PHC">Port Harcourt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="search">Search</Label>
              <Input
                placeholder="Search materials..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="action">Action</Label>
              <Button className="w-full" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Run Analysis
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Analyses</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredTrends.length}</div>
            <p className="text-xs text-muted-foreground">Trend analyses</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Growth</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              +{filteredTrends.length > 0 ? 
                (filteredTrends.reduce((sum, trend) => sum + (trend.averageGrowthRate || 0), 0) / filteredTrends.length).toFixed(1) 
                : 0}%
            </div>
            <p className="text-xs text-muted-foreground">Annual growth rate</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Volatility</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {filteredTrends.filter(t => (t.volatilityIndex || 0) >= 2.5).length}
            </div>
            <p className="text-xs text-muted-foreground">Volatile materials</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Confidence</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {filteredTrends.filter(t => (t.confidenceLevel || 0) >= 80).length}
            </div>
            <p className="text-xs text-muted-foreground">Reliable forecasts</p>
          </CardContent>
        </Card>
      </div>

      {/* Price Trends Table */}
      <Card>
        <CardHeader>
          <CardTitle>Price Trend Analysis</CardTitle>
          <CardDescription>Long-term price trends and forecasting</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Material</TableHead>
                <TableHead>Geo Zone</TableHead>
                <TableHead>Analysis Period</TableHead>
                <TableHead>Trend Direction</TableHead>
                <TableHead>Growth Rate</TableHead>
                <TableHead>Volatility</TableHead>
                <TableHead>Seasonality</TableHead>
                <TableHead>Forecast Q2</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead>Pattern</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTrends.map((trend) => (
                <TableRow key={trend.id}>
                  <TableCell className="font-medium">{getMaterialName(trend.materialId)}</TableCell>
                  <TableCell>{getGeoZoneName(trend.geoZoneId)}</TableCell>
                  <TableCell className="capitalize">{trend.analysisPeriod}</TableCell>
                  <TableCell className="flex items-center">
                    {getTrendIcon(trend.trendDirection || 'stable')}
                    <span className="capitalize ml-1">{trend.trendDirection}</span>
                  </TableCell>
                  <TableCell className="text-green-600 font-medium">
                    +{trend.averageGrowthRate?.toFixed(2)}%
                  </TableCell>
                  <TableCell className={getVolatilityColor(trend.volatilityIndex || 0)}>
                    {trend.volatilityIndex?.toFixed(1)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={trend.seasonalityFactor && trend.seasonalityFactor > 1.05 ? 'default' : 'secondary'}>
                      {trend.seasonalityFactor?.toFixed(2)}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">
                    {trend.forecastNextQuarter && formatCurrency(trend.forecastNextQuarter)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={trend.confidenceLevel && trend.confidenceLevel >= 80 ? 'default' : 'secondary'}>
                      {trend.confidenceLevel}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {trend.analysisData?.seasonalPattern?.replace('_', ' ')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Trend Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Volatility Analysis</CardTitle>
            <CardDescription>Materials with highest price volatility</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredTrends
                .sort((a, b) => (b.volatilityIndex || 0) - (a.volatilityIndex || 0))
                .slice(0, 5)
                .map((trend) => (
                  <div key={trend.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium">{getMaterialName(trend.materialId)}</div>
                      <div className="text-sm text-gray-600">{getGeoZoneName(trend.geoZoneId)}</div>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${getVolatilityColor(trend.volatilityIndex || 0)}`}>
                        {trend.volatilityIndex?.toFixed(1)}
                      </div>
                      <div className="text-sm text-gray-600">
                        {trend.analysisData?.volatilityReason?.replace('_', ' ')}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Growth Leaders</CardTitle>
            <CardDescription>Materials with highest growth rates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredTrends
                .sort((a, b) => (b.averageGrowthRate || 0) - (a.averageGrowthRate || 0))
                .slice(0, 5)
                .map((trend) => (
                  <div key={trend.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium">{getMaterialName(trend.materialId)}</div>
                      <div className="text-sm text-gray-600">{getGeoZoneName(trend.geoZoneId)}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-green-600">
                        +{trend.averageGrowthRate?.toFixed(1)}%
                      </div>
                      <div className="text-sm text-gray-600">
                        {trend.analysisData?.seasonalPattern?.replace('_', ' ')}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PriceTrends;




